/**
 * Pure utility functions shared between questionnaire wizard components.
 * Extracted from PublicQuestionnaireWizardPage and GenericQuestionnaireWizard
 * to avoid duplication.
 */

import { FormSection, QuestionField, QuestionnaireDefinitionData } from '../types/questionnaire.types';
import { evaluateFieldVisibility } from './conditionalLogic';

// ---------------------------------------------------------------------------
// Field collection
// ---------------------------------------------------------------------------

/** Recursively collects all data fields from sections/subsections, skipping
 *  non-data fields (button, content, info-banner, stripe_cc). */
export const getAllDataFields = (
  fields: QuestionField[],
  depth: number = 0
): Array<{ field: QuestionField; depth: number; indent: string }> => {
  const allFields: Array<{ field: QuestionField; depth: number; indent: string }> = [];
  const indent = '  '.repeat(depth);

  for (const field of fields) {
    // Skip non-data fields
    if (
      field.type === 'button' ||
      field.type === 'content' ||
      field.type === 'info-banner' ||
      field.type === 'stripe_cc'
    ) {
      continue;
    }

    // If it's a container type with nested fields, recurse
    if ((field.type === 'line' || field.type === 'container') && field.nestedFields) {
      const nestedFields = getAllDataFields(field.nestedFields, depth + 1);
      allFields.push(...nestedFields);
    } else {
      // Regular data field
      allFields.push({ field, depth, indent });
    }
  }

  return allFields;
};

// ---------------------------------------------------------------------------
// Section classification helpers
// ---------------------------------------------------------------------------

/** Returns true if all non-button fields in the section are button-group fields. */
export const hasOnlyButtonGroupFields = (section: FormSection | null | undefined): boolean => {
  if (!section) return false;
  const nonButtonFields = section.fields.filter((f) => f.type !== 'button');
  return nonButtonFields.length > 0 && nonButtonFields.every((f) => f.type === 'button-group');
};

/** Returns true if the section contains at least one content-type field. */
export const hasSomeContentFields = (section: FormSection | null | undefined): boolean => {
  if (!section) return false;
  return section.fields.some((f) => f.type === 'content');
};

/** Returns true if the section contains at least one regular input field
 *  (i.e. not button, content, info-banner, or button-group). */
export const hasRegularInputFields = (section: FormSection | null | undefined): boolean => {
  if (!section) return false;
  return section.fields.some(
    (f) =>
      f.type !== 'button' &&
      f.type !== 'content' &&
      f.type !== 'info-banner' &&
      f.type !== 'button-group'
  );
};

// ---------------------------------------------------------------------------
// Field validation
// ---------------------------------------------------------------------------

/** Validates a field value against its validation rules.
 *  Returns true when the value passes all rules (or when there are no rules). */
export const validateFieldValue = (
  field: QuestionField,
  value: any,
  answersMap: Record<string, any>
): boolean => {
  if (!field.validation) return true; // No validation rules means valid

  const validation = field.validation;
  const stringValue = String(value || '');

  // Pattern validation
  if (validation.pattern) {
    const regex = new RegExp(validation.pattern);
    if (!regex.test(stringValue)) {
      return false;
    }
  }

  // Min/Max length validation (for strings)
  if (validation.minLength && stringValue.length < validation.minLength) {
    return false;
  }
  if (validation.maxLength && stringValue.length > validation.maxLength) {
    return false;
  }

  // Min/Max value validation (for numbers)
  if (field.type === 'number') {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      if (validation.min !== undefined && numValue < validation.min) {
        return false;
      }
      if (validation.max !== undefined && numValue > validation.max) {
        return false;
      }
    }
  }

  // Custom validation types
  if (validation.validationType) {
    switch (validation.validationType) {
      case 'pastDate':
        if (value) {
          const date = new Date(value);
          if (date > new Date()) {
            return false; // Date must be in the past
          }
        }
        break;

      case 'futureDate':
        if (value) {
          const date = new Date(value);
          if (date < new Date()) {
            return false; // Date must be in the future
          }
        }
        break;

      case 'dateWithinDays':
        if (value && validation.dateWithinDays) {
          const date = new Date(value);
          const today = new Date();
          const diffTime = Math.abs(date.getTime() - today.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays > validation.dateWithinDays) {
            return false;
          }
        }
        break;

      case 'matchField':
        if (validation.matchFieldName) {
          const matchValue = answersMap[validation.matchFieldName];
          if (value !== matchValue) {
            return false; // Values must match
          }
        }
        break;

      case 'minAge':
        if (value && validation.minAge) {
          const birthDate = new Date(value);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          if (age < validation.minAge) {
            return false; // Age must be at least minAge
          }
        }
        break;
    }
  }

  return true; // All validations passed
};

// ---------------------------------------------------------------------------
// Text / content helpers
// ---------------------------------------------------------------------------

/** Replaces `{SCORE_NAME}` placeholders in text with actual score values from scoresData. */
export const replaceScorePlaceholders = (
  content: string,
  scoresData: Record<string, any>
): string => {
  if (!content) return content;

  // Replace all {SCORE_NAME} patterns with actual score values
  return content.replace(/\{([A-Z_0-9]+)\}/g, (_match, scoreName) => {
    const scoreData = scoresData[scoreName];
    if (scoreData && scoreData.score !== undefined) {
      return String(scoreData.score);
    }
    // Blank, not the raw placeholder: this content is shown to the patient.
    return '';
  });
};

/**
 * Substitutes {variable} tokens in content with resolved values.
 *
 * Only tokens whose key is present in `values` are replaced, so uppercase score
 * placeholders ({SCORE_NAME}, handled by replaceScorePlaceholders) and any
 * unknown tokens are left untouched. A resolved value that is empty renders as
 * empty — which is exactly the create_patient-without-a-lead case, where every
 * lead variable comes back as ''.
 */
export const substituteVariables = (
  content: string,
  values: Record<string, string>
): string => {
  if (!content || !values) return content;
  return content.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name] ?? '') : match
  );
};

/** Inserts a red asterisk (*) before the first list in HTML content when the
 *  field is required. */
export const insertAsteriskBeforeList = (html: string, isRequired: boolean): string => {
  if (!isRequired) return html;

  // Find first list tag (<ul> or <ol>)
  const listMatch = html.match(/(<ul>|<ol>)/);

  if (listMatch && listMatch.index !== undefined) {
    // Find the last </p> before the list
    const beforeList = html.substring(0, listMatch.index);
    const lastPClosingTag = beforeList.lastIndexOf('</p>');

    if (lastPClosingTag !== -1) {
      // Insert asterisk inside the paragraph, before </p>
      return (
        html.substring(0, lastPClosingTag) +
        '<span style="color: red;">*</span>' +
        html.substring(lastPClosingTag)
      );
    } else {
      // No closing p tag found - insert before the list
      return beforeList + '<span style="color: red;">*</span> ' + html.substring(listMatch.index);
    }
  } else {
    // No list found - append asterisk to end, before last </p> if it exists
    const lastPClosing = html.lastIndexOf('</p>');
    if (lastPClosing !== -1) {
      return (
        html.substring(0, lastPClosing) +
        '<span style="color: red;">*</span>' +
        html.substring(lastPClosing)
      );
    }
    return html + '<span style="color: red;">*</span>';
  }
};

// ---------------------------------------------------------------------------
// Styling helpers
// ---------------------------------------------------------------------------

/** Returns MUI `sx`-compatible CSS-in-JS object for list styling based on
 *  the `listStyle` configuration value of a content field. */
export const getListStyles = (listStyle: string): Record<string, any> => {
  if (listStyle === 'check') {
    return {
      '& ul': { mb: 1, mt: 0, pl: 0, listStyle: 'none', display: 'block' },
      '& ol': { mb: 1, mt: 0, pl: 0, listStyle: 'none', display: 'block' },
      '& li': {
        mb: 0.5,
        display: 'flex',
        alignItems: 'flex-start',
        paddingLeft: 0,
        '&::before': {
          content: '"✓"',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: '2px solid #3A7380',
          backgroundColor: 'transparent',
          color: '#3A7380',
          fontSize: '12px',
          fontWeight: 'bold',
          marginRight: '8px',
          flexShrink: 0,
          marginTop: '2px',
        },
      },
    };
  } else if (listStyle === 'dash') {
    return {
      '& ul': { mb: 1, mt: 0, pl: 0, listStyle: 'none', display: 'block' },
      '& ol': { mb: 1, mt: 0, pl: 0, listStyle: 'none', display: 'block' },
      '& li': {
        mb: 0.5,
        display: 'flex',
        alignItems: 'flex-start',
        paddingLeft: 0,
        '&::before': {
          content: '"−"',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: '2px solid #3A7380',
          backgroundColor: 'transparent',
          color: '#3A7380',
          fontSize: '16px',
          fontWeight: 'bold',
          marginRight: '8px',
          flexShrink: 0,
          marginTop: '2px',
        },
      },
    };
  } else if (listStyle === 'custom-icons') {
    // Custom icons style - no bullets, flex layout for icon + text
    return {
      '& ul': { mb: 1, mt: 0, pl: 0, listStyle: 'none', display: 'block' },
      '& ol': { mb: 1, mt: 0, pl: 0, listStyle: 'none', display: 'block' },
      '& li': {
        mb: 0.5,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 0,
      },
    };
  } else {
    // Default style with regular bullets
    return {
      '& ul': { mb: 1, mt: 0, pl: 3, listStyleType: 'disc', display: 'block' },
      '& ol': { mb: 1, mt: 0, pl: 3, listStyleType: 'decimal', display: 'block' },
      '& li': { mb: 0.5, display: 'list-item', marginLeft: '1em' },
    };
  }
};

// ---------------------------------------------------------------------------
// Answer conversion (used only in PublicQuestionnaireWizardPage)
// ---------------------------------------------------------------------------

/** Converts a form field value to the API answer array format.
 *  File fields are serialised as JSON strings containing id/name/size. */
export const convertToAnswer = (value: any, fieldType: string): string[] => {
  if (fieldType === 'file') {
    // File fields: save as JSON strings with id, name and size
    if (Array.isArray(value)) {
      return value.map((file: any) =>
        JSON.stringify({
          id: file.id,
          name: file.name,
          size: file.size || 0,
        })
      );
    } else if (value) {
      return [
        JSON.stringify({
          id: value.id,
          name: value.name,
          size: value.size || 0,
        }),
      ];
    } else {
      return [];
    }
  }
  // Other fields: standard conversion
  return Array.isArray(value) ? value : value ? [String(value)] : [];
};

// ---------------------------------------------------------------------------
// Validation field list (used only in PublicQuestionnaireWizardPage)
// ---------------------------------------------------------------------------

/** Returns the list of field names in a section that should be validated
 *  (skips button, content, and stripe_cc fields; flattens nested fields).
 *  When formValues is provided, also skips fields hidden by conditional logic
 *  so a hidden-but-required field doesn't block navigation. */
export const getFieldsToValidate = (
  section: FormSection,
  formValues: Record<string, any> = {}
): string[] => {
  const isVisible = (f: QuestionField): boolean =>
    evaluateFieldVisibility(f.name, formValues, undefined, f.conditionalLogic);

  return section.fields.flatMap((f) => {
    // Skip button, content, and stripe_cc fields
    if (f.type === 'button' || f.type === 'content' || f.type === 'stripe_cc') {
      return [];
    }
    // Skip fields whose conditional rule fails — and skip their nested children too
    if (!isVisible(f)) {
      return [];
    }
    // For line and container fields, return nested field names
    if (f.type === 'line' || f.type === 'container') {
      return (f.nestedFields || []).flatMap((nf) => {
        // Skip button, content, and stripe_cc nested fields
        if (nf.type === 'button' || nf.type === 'content' || nf.type === 'stripe_cc') {
          return [];
        }
        if (!isVisible(nf)) {
          return [];
        }
        // Check if nested field is also a line field (triple nesting: container → line → fields)
        if (nf.type === 'line') {
          return (nf.nestedFields || [])
            .filter((lf) => lf.type !== 'button' && lf.type !== 'content' && lf.type !== 'stripe_cc')
            .filter((lf) => isVisible(lf))
            .map((lf) => lf.name);
        }
        // Regular nested field
        return [nf.name];
      });
    }
    // For regular fields, return the field name
    return [f.name];
  });
};

// ---------------------------------------------------------------------------
// Starting section determination
// ---------------------------------------------------------------------------

/**
 * Given a loaded definition and a pre-parsed answers map, returns the section
 * index the wizard should open on.
 *
 * Algorithm:
 *  1. Find the last section that has at least one saved value.
 *  2. Validate every section up to that index (required-field check + field
 *     value validation + server-computed section validations via
 *     `failedValidationSectionIds` from GET /public/questionnaires/{token}).
 *  3. Return the first section with errors, or lastFilled+1 if all are valid,
 *     or 0 if nothing is filled yet.
 */
export function determineStartingSection(
  definition: QuestionnaireDefinitionData,
  answersMap: Record<string, any>,
  failedValidationSectionIds: string[] = []
): number {
  const sections = definition.sections;
  if (!sections?.length) return 0;

  // Find the last section that has any saved input value
  let lastFilledSectionIndex = -1;
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    for (const field of section.fields) {
      if (field.type === 'button' || field.type === 'content' || field.type === 'info-banner') continue;
      const answer = answersMap[field.name];
      if (answer !== undefined && answer !== null && answer !== '' &&
          !(Array.isArray(answer) && answer.length === 0)) {
        lastFilledSectionIndex = i;
        break;
      }
    }
  }

  if (lastFilledSectionIndex < 0) return 0;

  // Validate sections 0..lastFilledSectionIndex
  let firstErrorSectionIndex = -1;
  for (let i = 0; i <= lastFilledSectionIndex; i++) {
    const allFields = getAllDataFields(sections[i].fields);
    let sectionHasErrors = false;

    // STEP 1: required fields must have values
    for (const { field } of allFields) {
      if (field.required) {
        const value = answersMap[field.name];
        const isEmpty =
          value === undefined || value === null || value === '' ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'boolean' && value === false && field.type === 'checkbox');
        if (isEmpty) { sectionHasErrors = true; break; }
      }
    }

    // STEP 2: validate values that are present
    if (!sectionHasErrors) {
      for (const { field } of allFields) {
        const value = answersMap[field.name];
        if (value === undefined || value === null || value === '' ||
            (Array.isArray(value) && value.length === 0)) continue;
        if (!validateFieldValue(field, value, answersMap)) {
          sectionHasErrors = true;
          break;
        }
      }
    }

    // STEP 3: server-computed section validations (e.g., titration vitals)
    if (!sectionHasErrors && failedValidationSectionIds.includes(sections[i].id)) {
      sectionHasErrors = true;
    }

    if (sectionHasErrors) { firstErrorSectionIndex = i; break; }
  }

  if (firstErrorSectionIndex >= 0) return firstErrorSectionIndex;
  return Math.min(lastFilledSectionIndex + 1, sections.length - 1);
}

// ---------------------------------------------------------------------------
// Answer deserialization
// ---------------------------------------------------------------------------

/**
 * Converts raw server answers (arrays of strings) into typed form values.
 * Handles: file (single + multiple), checkbox, number, multiselect/checkboxes,
 * and plain scalar fields.
 */
export function buildAnswersMap(
  answers: { question_name: string; answer: any }[],
  sections: { fields: any[] }[]
): Record<string, any> {
  const map: Record<string, any> = {};

  answers?.forEach((answer) => {
    let fieldType: string | undefined;
    let fieldConfig: any;

    for (const section of sections) {
      const field = section.fields.find((f: any) => f.name === answer.question_name);
      if (field) { fieldType = field.type; fieldConfig = field.fileConfig; break; }
    }

    let value: any = answer.answer;

    if (fieldType === 'number' && Array.isArray(value) && value.length > 0) {
      value = parseFloat(value[0]);
    } else if (fieldType === 'file' && Array.isArray(value)) {
      const allowMultiple = fieldConfig?.allowMultiple || false;
      const parseItem = (item: any) => {
        if (typeof item === 'string') {
          try { return JSON.parse(item); } catch { return { name: item, size: 0 }; }
        }
        if (item && typeof item === 'object' && item.name) {
          return { id: item.id, name: item.name, size: item.size || 0 };
        }
        return { name: String(item), size: 0 };
      };
      if (allowMultiple) {
        value = value.map(parseItem);
      } else {
        value = value.length > 0 ? parseItem(value[0]) : null;
      }
    } else if (fieldType === 'checkbox') {
      const raw = Array.isArray(value) && value.length === 1 ? value[0] : value;
      value = String(raw) === 'true';
    } else if (
      Array.isArray(value) && value.length === 1 &&
      fieldType !== 'checkboxes' && fieldType !== 'multiselect' && fieldType !== 'file'
    ) {
      value = value[0];
    }

    map[answer.question_name] = value;
  });

  return map;
}
